<template lang="pug">
  section
    table.coaching-list
      thead
        tr
          th User
          th Status
          th Coach
          th Current Focus
          th Last Coaching Interaction
          th Access Code
          th Livongo Member
          th.has-tooltip(v-on:mouseenter="showLegend(true)" v-on:mouseleave="!showLegend(false)")
            span.link-highlight Assessment ?
            transition(name="zoom-fade")
              .tooltip-content(v-if="showAssessmentLegend")
                section.tooltip-body
                  ul.icon-list
                    li
                      .icon
                        .swatch.severe
                      .label Severe / Extremely Severe
                    li
                      .icon
                        .swatch.moderate
                      .label Moderate
                    li
                      .icon
                        .swatch.mild
                      .label Mild
                    li
                      .icon
                        .swatch.normal
                      .label Normal
                    li
                      .icon &#9650;
                      .label Improving Functioning
                    li
                      .icon &#9660;
                      .label Declining Functioning
      tbody(v-if="loading")
        tr
          td(colspan="7")
            .loading-dots
              .dot1
              .dot2
              .dot3
      tbody(v-else-if="patients.length == 0")
        tr
          td(colspan="7")
            | No users to display
      tbody(v-else)
        template(v-for="patient in patients")
          tr(
            v-on:click.stop='selectPatient(patient)'
            :class="{ 'has-crisis': patientInCrisis(patient.crisisStatus) }")
            td
              router-link(:to="{ name: 'Patient', params: { id: patient.id }, query: { coaching: true }}" @click.native='onRouteClick')
                span.link-highlight {{ patient.firstName }} {{ patient.lastName }}
                br
                small.text-overflow {{ patient.emailAddress }}
            td
              span.text-overflow {{ patient.coachingStatus | snakeToCamelCase }}
              span.crisis-status.text-overflow(v-show="patientInCrisis(patient.crisisStatus)")
                | Needs Review:
                br
                | {{ patient.crisisStatus }}
            td
              | {{ patient.coachName }}
              br
              small.text-overflow {{ patient.coachEmail }}
            td {{ patient.currentFocus }}
            td
              template(v-if="patient.lastCoachingInteraction")
                | {{ patient.lastCoachingInteraction | longDate }}
                br
                template(v-if="patient.messageType === 'FROM_COACH'")
                  | By Coach
                template(v-if="patient.messageType === 'TO_COACH'")
                  | By User
              template(v-if="!patient.lastCoachingInteraction")
                | No messages sent
            td
              span.text-overflow {{ patient.referrerName || 'No referrer'}}
              span.text-overflow {{ patient.accessCode }}
            td
              template(v-if="patient.isLivongoMember")
                | Yes
              template(v-else)
                | No
            td
              assessment-icon(:assessment="assessment" v-for="(assessment, index) in patient.assessmentScores" :key="index")
              div(v-if="patient.teletherapyEligible")
                svg(xmlns='http://www.w3.org/2000/svg' viewBox="0 0 97 24")
                  g(fill='none' fill-rule='evenodd')
                    rect(fill='#FFF' width='97' height='24' rx='2')
                    g(fill='#007CA6' fill-rule='nonzero')
                      path(d='M17.667 18a.47.47 0 00.466-.35c.03-.206-.116-.4-.335-.465l-2.931-.724v-.983c2.493-.103 4.273-.375 4.36-.387.452-.078.773-.414.773-.815V7.24c0-.4-.335-.75-.787-.814C19.096 6.414 16.31 6 13 6s-6.11.414-6.227.427C6.335 6.49 6 6.84 6 7.24v7.035c0 .4.32.737.773.815.087.012 1.867.284 4.36.387v.983l-2.93.724c-.22.065-.365.259-.336.466a.47.47 0 00.466.349h9.334zM13 13.914a49.065 49.065 0 01-5.133-.31V7.861A47.66 47.66 0 0113 7.565c2.144 0 4.098.18 5.133.297v5.741a49.065 49.065 0 01-5.133.31zM31.05 15.54V9.98h1.89V9H28v.98h1.89v5.56h1.16zm8.02 0v-.98h-2.88v-1.93h2.35v-.98h-2.35V9.98h2.78V9h-3.94v6.54h4.04zm6.22 0v-.98h-2.72V9h-1.16v6.54h3.88zm6.18 0v-.98h-2.88v-1.93h2.35v-.98h-2.35V9.98h2.78V9h-3.94v6.54h4.04zm3.5 0v-2.9h2.65v2.9h1.16V9h-1.16v2.63h-2.65V9h-1.16v6.54h1.16zm10.51 0v-.98H62.6v-1.93h2.35v-.98H62.6V9.98h2.78V9h-3.94v6.54h4.04zm2.67 0l.51-1.77h2.2l.51 1.77h1.22L70.46 9H69.1l-2.13 6.54h1.18zm2.45-2.68h-1.67l.23-.8c.21-.69.4-1.43.58-2.16h.04c.19.72.38 1.47.59 2.16l.23.8zm7.68 2.68v-.98h-2.72V9H74.4v6.54h3.88zm3.42 0V9.98h1.89V9h-4.94v.98h1.89v5.56h1.16zm5.14 0v-2.9h2.65v2.9h1.16V9h-1.16v2.63h-2.65V9h-1.16v6.54h1.16z')

</template>

<script>
import snakeToCamelCase from '@/filters/snakeToCamelCase.js';
import lastInteractionMixin from '@/mixins/lastInteraction.js';
import AssessmentIcon from '@/components/patient/AssessmentIcon';

export default {
  name: 'CoachingList',
  data() {
    return {
      showAssessmentLegend: false
    };
  },
  props: {
    patients: {
      type: Array,
      required: true
    },
    loading: {
      type: Boolean
    },
    selectPatient: {
      type: Function,
      required: true
    },
    selectedPatientId: {
      type: Number
    }
  },
  components: {
    AssessmentIcon
  },
  filters: {
    snakeToCamelCase
  },
  mixins: [ lastInteractionMixin ],
  methods: {
    showLegend(show) {
      this.showAssessmentLegend = show;
    },
    patientInCrisis(status) {
      return status !== 'NONE';
    },
    onRouteClick(event) {
      event.stopPropagation();
    }
  }
};
</script>

<style lang="scss">
  .coaching-list {
    width: 100%;
    margin: 0 0 $base-spacing 0;
    border: 1px solid $base-border-color;
    border-bottom: none;
    border-collapse: separate;
    border-spacing: 0;
    th, td {
      border: none;
      padding: $base-spacing $small-spacing;
      line-height: 1.2;
      font-size: $ms-down1;
      white-space: pre-wrap;
      vertical-align: top;
    }
    thead th {
      background: #fff;
      border-bottom: 1px solid $base-font-color;
    }
    tbody td {
      border-bottom: 1px solid $base-border-color;
    }
    .text-overflow {
      display: block;
      margin-top: 4px;
      @include text-overflow;
    }
    .checkbox {
      @include size(1em);
      border-radius: 1em;
      border: 1px solid $base-font-color;
      vertical-align: middle;
      box-shadow: inset 0 2px 4px rgba(#000, .2);
      &.selected {
        box-shadow: 0 0 0 rgba(#fff, 0);
        background: $success-color;
        border-color: darken($success-color, 5%);
      }
    }
    tbody tr {
      // Placed at top for easy overwriting
      &:nth-child(2n) {
        background: #fff;
      }
      &.alert {
        outline: 2px solid $error-color;
        background: $md-yellow-100 !important;
      }
      &.has-crisis {
        background-color: #f2dede;
      }

      // Placed at bottom so that hover/focus rules always apply
      &:hover,
      &:focus {
        cursor: pointer;
        background: $light-color;
      }
    }

    .crisis-status {
      color: #a94442;
      font-weight: bold;
    }
  }
</style>
